import Modal from './modal.js';

/**
 * PopupModal - Extends Modal with trigger functionality
 * Supports time-delay, exit-intent, scroll-depth, and manual triggers
 *
 * @extends Modal
 *
 * @example
 * <popup-modal
 *   data-toggle-attribute="data-toggle-popup-12345"
 *   data-trigger="time-delay"
 *   data-delay="5000"
 *   data-popup-id="12345"
 *   data-frequency="once"
 * >
 *   <!-- Modal content -->
 * </popup-modal>
 */

const POPUP_CONFIG = {
  TRIGGERS: {
    EXIT_INTENT: 'exit-intent',
    SCROLL_DEPTH: 'scroll-depth',
    IMMEDIATE: 'immediate',
    MANUAL: 'manual'
  },
  STORAGE_KEY: 'popup_shown_',
  MOUSE_LEAVE_THRESHOLD: 10,
  SCROLL_THRESHOLD_DEFAULT: 50
};

export default class PopupModal extends Modal {
  constructor() {
    super();
    this._exitIntentHandler = null;
    this._scrollHandler = null;
    this._timeoutId = null;
    this._hasBeenShown = false;
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize triggers after a short delay to ensure page is ready
    requestAnimationFrame(() => {
      this.initializeTriggers();
    });
  }

  /**
   * Override to open popup when section is selected in theme editor
   * @returns {boolean} True to open on section select
   */
  shouldOpenOnSectionSelect() {
    return true;
  }

  /**
   * Initialize popup triggers based on settings
   */
  initializeTriggers() {
    const trigger = this.dataset.trigger || POPUP_CONFIG.TRIGGERS.MANUAL;
    const popupId = this.dataset.popupId;
    const useDelay = this.dataset.useDelay === 'true';

    // Check if popup should be skipped based on frequency settings
    if (this.shouldSkipPopup(popupId)) {
      return;
    }

    // Setup time delay if enabled (can be combined with other triggers)
    if (useDelay) {
      this.setupTimeDelayTrigger();
    }

    // Setup primary trigger
    switch (trigger) {
      case POPUP_CONFIG.TRIGGERS.IMMEDIATE:
        // Show immediately (after time delay if enabled)
        if (!useDelay) {
          this.triggerPopup();
        }
        break;
      case POPUP_CONFIG.TRIGGERS.EXIT_INTENT:
        this.setupExitIntentTrigger();
        break;
      case POPUP_CONFIG.TRIGGERS.SCROLL_DEPTH:
        this.setupScrollTrigger();
        break;
      case POPUP_CONFIG.TRIGGERS.MANUAL:
        // Manual trigger - wait for button click
        break;
    }
  }

  /**
   * Setup time-delayed popup trigger
   */
  setupTimeDelayTrigger() {
    const delay = parseInt(this.dataset.delay || '5', 10) * 1000;
    this._timeoutId = setTimeout(() => this.triggerPopup(), delay);
  }

  /**
   * Setup exit-intent popup trigger
   * Detects mouse leaving viewport from top
   */
  setupExitIntentTrigger() {
    this._exitIntentHandler = (e) => {
      // Only trigger if mouse leaves from top and hasn't been shown
      if (e.clientY < POPUP_CONFIG.MOUSE_LEAVE_THRESHOLD && !this._hasBeenShown) {
        this.triggerPopup();
      }
    };
    document.addEventListener('mouseleave', this._exitIntentHandler);
  }

  /**
   * Setup scroll-depth popup trigger
   * Triggers when user scrolls past threshold percentage
   */
  setupScrollTrigger() {
    const threshold = parseInt(
      this.dataset.scrollThreshold || POPUP_CONFIG.SCROLL_THRESHOLD_DEFAULT,
      10
    );

    this._scrollHandler = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (window.scrollY / scrollHeight) * 100;

      if (scrollPercent >= threshold && !this._hasBeenShown) {
        this.triggerPopup();
      }
    };

    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  /**
   * Trigger the popup to open
   */
  triggerPopup() {
    if (this._hasBeenShown) return;

    this._hasBeenShown = true;
    this.open();

    // Record that popup was shown
    const popupId = this.dataset.popupId;
    const frequency = this.dataset.frequency || 'once';
    this.recordPopupShown(popupId, frequency);

    // Clean up triggers after first show
    this.cleanupTriggers();
  }

  /**
   * Check if popup should be skipped based on frequency settings
   * @param {string} popupId - Unique popup identifier
   * @returns {boolean} True if popup should be skipped
   */
  shouldSkipPopup(popupId) {
    if (!popupId) return false;

    const frequency = this.dataset.frequency || 'once';
    const storageKey = POPUP_CONFIG.STORAGE_KEY + popupId;
    const lastShown = localStorage.getItem(storageKey);

    if (!lastShown) return false;

    switch (frequency) {
      case 'once':
        return true;
      case 'session':
        return sessionStorage.getItem(storageKey) !== null;
      case 'daily':
        const daysSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
        return daysSince < 1;
      case 'always':
        return false;
      default:
        return false;
    }
  }

  /**
   * Record that popup was shown
   * @param {string} popupId - Unique popup identifier
   * @param {string} frequency - Frequency setting (once, session, daily, always)
   */
  recordPopupShown(popupId, frequency) {
    if (!popupId) return;

    const storageKey = POPUP_CONFIG.STORAGE_KEY + popupId;
    localStorage.setItem(storageKey, Date.now().toString());

    if (frequency === 'session') {
      sessionStorage.setItem(storageKey, 'true');
    }
  }

  /**
   * Clean up all event listeners and timeouts
   */
  cleanupTriggers() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
    if (this._exitIntentHandler) {
      document.removeEventListener('mouseleave', this._exitIntentHandler);
      this._exitIntentHandler = null;
    }
    if (this._scrollHandler) {
      window.removeEventListener('scroll', this._scrollHandler);
      this._scrollHandler = null;
    }
  }

  /**
   * Cleanup on disconnect
   */
  disconnectedCallback() {
    this.cleanupTriggers();
    super.disconnectedCallback();
  }
}

customElements.define('popup-modal', PopupModal);
